import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Calendar, DollarSign, Users, Target,
  TrendingUp, AlertCircle, FileText, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/common/StatusBadge';
import ProgressBar from '../components/common/ProgressBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const ProjectDetailPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const userStr = localStorage.getItem('xchange_user');
      const user = JSON.parse(userStr);
      setUserRole(user.role);

      // Fetch project details
      const projectRes = await fetch(`${apiUrl}/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectData = await projectRes.json();
      setProject(projectData);

      // Fetch project stats
      const statsRes = await fetch(`${apiUrl}/api/projects/${projectId}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch milestones
      const milestonesRes = await fetch(`${apiUrl}/api/milestones?projectId=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const milestonesData = await milestonesRes.json();
      setMilestones(milestonesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/enhanced/sponsor/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
              <p className="text-gray-600 mt-1">{project.projectCode}</p>
            </div>
            <div className="flex gap-3">
              <StatusBadge status={project.status} />
              <Button
                onClick={() => navigate(`/milestones/${projectId}`)}
                className="text-white"
                style={{ background: '#14B8A6' }}
              >
                View Milestones
              </Button>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {project.alerts && project.alerts.length > 0 && (
          <AlertBanner
            type="warning"
            message={`This project has ${project.alerts.length} active alert(s). Review them below.`}
            className="mb-6"
          />
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={DollarSign}
            label="Total Budget"
            value={`₹${(project.totalBudget / 100000).toFixed(1)}L`}
            color="#14B8A6"
          />
          <StatCard
            icon={TrendingUp}
            label="Utilized"
            value={`₹${(project.fundsUtilized / 100000).toFixed(1)}L`}
            subtitle={`${stats?.budget?.utilizationRate?.toFixed(0)}%`}
            color="#1E3A5F"
          />
          <StatCard
            icon={CheckCircle}
            label="Completion"
            value={`${project.completionPercentage?.toFixed(0)}%`}
            subtitle={`${stats?.milestones?.completed}/${stats?.milestones?.total} milestones`}
            color="#10B981"
          />
          <StatCard
            icon={AlertCircle}
            label="Alerts"
            value={stats?.alerts || 0}
            color="#EF4444"
          />
        </div>

        {/* Project Info */}
        <div className="grid grid-cols-3 gap-8 mb-8">
          <div className="col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Project Overview</h2>
              <p className="text-gray-700 mb-4">{project.description}</p>
              
              <div className="grid grid-cols-2 gap-4">
                <InfoItem icon={Target} label="Category" value={project.scheduleVIICategory} />
                <InfoItem icon={MapPin} label="Location" value={`${project.location.district}, ${project.location.state}`} />
                <InfoItem icon={Calendar} label="Duration" value={`${project.duration} months`} />
                <InfoItem icon={Users} label="Beneficiaries" value={`${project.targetBeneficiaries.count} ${project.targetBeneficiaries.category}`} />
              </div>
            </div>

            {/* SMART Goals */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">SMART Goals</h2>
              <div className="space-y-3">
                <GoalItem label="Specific" value={project.smartGoals.specific} />
                <GoalItem label="Measurable" value={project.smartGoals.measurable} />
                <GoalItem label="Achievable" value={project.smartGoals.achievable} />
                <GoalItem label="Relevant" value={project.smartGoals.relevant} />
                <GoalItem label="Time-bound" value={project.smartGoals.timeBound} />
              </div>
            </div>
          </div>

          <div>
            {/* Progress Card */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Progress Overview</h3>
              <ProgressBar
                progress={project.completionPercentage}
                label="Overall Completion"
                color="auto"
              />
              <div className="mt-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Start Date</span>
                  <span className="font-semibold">{new Date(project.startDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">End Date</span>
                  <span className="font-semibold">{new Date(project.endDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Budget Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Budget Breakdown</h3>
              <div className="space-y-3">
                {project.budgetBreakdown?.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{item.category}</span>
                      <span className="font-semibold">₹{(item.amount / 100000).toFixed(1)}L</span>
                    </div>
                    <ProgressBar
                      progress={item.percentage}
                      showLabel={false}
                      height="h-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Milestones Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Milestones</h2>
            <Button
              onClick={() => navigate(`/milestones/${projectId}`)}
              variant="outline"
            >
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {milestones.slice(0, 3).map((milestone) => (
              <MilestoneItem key={milestone.id} milestone={milestone} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, subtitle, color }) => (
  <div className="bg-white rounded-xl shadow-md p-6">
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-gray-500">{label}</p>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
  </div>
);

// Info Item
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-5 h-5 text-teal-500" />
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  </div>
);

// Goal Item
const GoalItem = ({ label, value }) => (
  <div>
    <p className="text-sm font-semibold text-gray-700 mb-1">{label}</p>
    <p className="text-sm text-gray-600">{value}</p>
  </div>
);

// Milestone Item
const MilestoneItem = ({ milestone }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <h4 className="font-semibold text-gray-900">{milestone.title}</h4>
      <StatusBadge status={milestone.status} size="sm" />
    </div>
    <ProgressBar
      progress={milestone.completionPercentage}
      label=""
      height="h-2"
    />
  </div>
);

export default ProjectDetailPage;